import { useEffect, useState } from "react";
import { Spinner, useToast } from "@chakra-ui/react";
import {
  CreateUpdateSignatureBackgroundInput,
  SignatureBackground,
  useCreateSignatureBackgroundMutation,
  useGetSignatureBackgroundLazyQuery,
  useUpdateSignatureBackgroundMutation,
} from "../../generated/graphql";
import { useErrorMessage } from "../../hooks/errorMessage";
import {
  FormMutationType,
  InheritFullWidthModalProps,
} from "../../components/FullWidthModal/interface";
import FullWidthModal from "../../components/FullWidthModal";
import MCFormSignatureBackground from "../../components/MCFormSignatureBackground";

const SignatureBackgroundForm: React.FC<
  InheritFullWidthModalProps<SignatureBackground>
> = ({ id, onClose, isOpen, ...props }) => {
  const [getSignatureBackground, { data, loading: isLoadingData }] =
    useGetSignatureBackgroundLazyQuery();

  useEffect(() => {
    if (data) {
      const { name, image, enable } =
        data?.getSignatureBackground as SignatureBackground;

      setSignatureBackgroundValue({
        name,
        image,
        enable,
      });

      setImageUrl(data.getSignatureBackground.imageUrl);
    }
  }, [data]);

  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const toast = useToast();

  const { showErrorMessage } = useErrorMessage();

  const [signatureBackgroundValue, setSignatureBackgroundValue] =
    useState<CreateUpdateSignatureBackgroundInput>();
  const [createSignatureBackgroundMutation] = useCreateSignatureBackgroundMutation();
  const [updateSignatureBackgroundMutation] = useUpdateSignatureBackgroundMutation();

  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (id && isOpen) {
      getSignatureBackground({ variables: { id } });
    }
  }, [id, isOpen, getSignatureBackground]);

  const reset = () => {
    setSignatureBackgroundValue(undefined);
    setImageUrl("");
  };

  const saveSignatureBackground = async (input: CreateUpdateSignatureBackgroundInput) => {
    try {
      setIsLoadingSave(true);

      if (id) {
        const res = await updateSignatureBackgroundMutation({
          variables: {
            id,
            input,
          },
        });
        toast({
          description: "Signature Background successfully updated!",
          status: "success",
        });
        onClose({
          type: FormMutationType.UPDATE,
          data: res.data?.updateSignatureBackground as SignatureBackground,
        });
      } else {
        await createSignatureBackgroundMutation({
          variables: {
            input,
          },
        });
        toast({
          description: "Signature Background successfully added!",
          status: "success",
        });
        onClose({ type: FormMutationType.CREATE });
      }

      reset();
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setIsLoadingSave(false);
    }
  };

  return (
    <FullWidthModal
      title={id ? "Edit Signature Background" : "New Signature Background"}
      onClose={() => {
        reset();
        onClose();
      }}
      isOpen={isOpen}
      {...props}
    >
      {isLoadingData ? (
        <Spinner />
      ) : (
        <MCFormSignatureBackground
          value={signatureBackgroundValue}
          imageUrl={imageUrl || ""}
          onBack={() => {
            onClose();
          }}
          onSubmit={(value) => {
            saveSignatureBackground(value);
          }}
          isLoadingSave={isLoadingSave}
        />
      )}
    </FullWidthModal>
  );
};

export default SignatureBackgroundForm;
